import React, { useState, useEffect } from 'react';
import { ArrowRight, AlertTriangle, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { mockImprovementSuggestions } from '../../data/mockAnalyticsData';
import LoadingSkeleton from '../ui/LoadingSkeleton';
import { supabaseClient } from '../../utils/supabaseClient';
import { backendUrl } from '../../utils/socketClient';

interface Suggestion {
  title: string;
  content: string;
  priority: 'high' | 'medium' | 'low';
}

interface Props {
  shopName: string;
  dateRange: { start: Date; end: Date };
  periodLabel: string;
  isLoading?: boolean;
}

const ImprovementSuggestions: React.FC<Props> = ({ shopName, dateRange, periodLabel, isLoading: externalLoading }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = externalLoading || internalLoading;
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (externalLoading) {
      setInternalLoading(true);
    } else {
      setInternalLoading(false);
    }
  }, [externalLoading]);

  // Supabaseからキャッシュ取得
  const fetchCachedSuggestions = async () => {
    setInternalLoading(true);
    setError(null);
    const { data, error } = await supabaseClient
      .from('suggestion_cache')
      .select('suggestions_json')
      .eq('shopname', shopName)
      .eq('period_label', periodLabel)
      .order('created_at', { ascending: false })
      .limit(1);
    if (data && data.length > 0 && data[0].suggestions_json) {
      setSuggestions(data[0].suggestions_json);
    } else {
      setSuggestions([]);
    }
    setInternalLoading(false);
  };

  // AI生成API呼び出し＆Supabase保存
  const updateSuggestions = async () => {
    setIsUpdating(true);
    setError(null);
    try {
      // console.log('dateRange:', dateRange);
      // 日付を経過日数に変換
      const startDate = Math.floor((new Date().getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
      const endDate = Math.floor((new Date().getTime() - dateRange.end.getTime()) / (1000 * 60 * 60 * 24));
      const url = `${backendUrl}/suggestions/${encodeURIComponent(shopName)}`;
      // console.log('Request URL:', url);
      // console.log('Request body:', {
      //   startDate,
      //   endDate,
      //   periodLabel,
      // });
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate,
          endDate,
          periodLabel,
        }),
      });
      // console.log('Response status:', res.status);
      const data = await res.json();
      // console.log('Response data:', data);
      if (Array.isArray(data)) {
        setSuggestions(data);
      } else {
        // console.log('AI提案APIレスポンス:', data);
        setSuggestions([]);
        setError('AI提案の生成に失敗しました: ' + (data?.error || JSON.stringify(data)));
      }
    } catch (e) {
      setError('AI提案の生成に失敗しました: ' + (e instanceof Error ? e.message : String(e)));
      setSuggestions([]);
    }
    setIsUpdating(false);
  };

  useEffect(() => {
    fetchCachedSuggestions();
  }, [shopName, periodLabel]);

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'medium':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'low':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-primary" />;
    }
  };

  if (loading) {
    return <LoadingSkeleton className="h-[500px]" />;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 shadow-sm">
        <div className="flex items-center mb-4">
          <h3 className="text-lg font-medium text-gray-800">AI改善提案</h3>
          <button
            onClick={updateSuggestions}
            disabled={isUpdating}
            className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md ${
              isUpdating
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
            {isUpdating ? '更新中...' : '更新'}
          </button>
        </div>
        {error ? (
          <div className="text-red-500">{error}</div>
        ) : suggestions.length === 0 ? (
          <div className="text-gray-500">提案が見つかりません。更新ボタンを押してください。</div>
        ) : (
          <div className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 transition-all hover:shadow-md">
                <div className="flex items-start gap-3">
                  {getPriorityIcon(suggestion.priority)}
                  <div>
                    <h4 className="font-medium text-gray-900">{suggestion.title}</h4>
                    <p className="mt-1 text-sm text-gray-600">{suggestion.content}</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      suggestion.priority === 'high'
                        ? 'bg-red-100 text-red-800'
                        : suggestion.priority === 'medium'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {suggestion.priority} priority
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImprovementSuggestions;